export interface RegressionOption {
  value: string;
  parameters: {
    en: string;
    cs: string;
    sk: string;
  };
}

/**
 * Translated label for "Regression Parameters" dropdown title
 */
const REGRESSION_DROPDOWN_LABEL: Record<string, string> = {
  en: "Regression Parameters",
  cs: "Regresní parametry",
  sk: "Regresné parametre",
};

/**
 * Translated label for "Option" prefix in dropdown items
 */
const REGRESSION_OPTION_PREFIX: Record<string, string> = {
  en: "Option",
  cs: "Varianta",
  sk: "Varianta",
};

/**
 * Translated label for "Regression parameters used in analysis:" note
 */
const REGRESSION_NOTE_LABEL: Record<string, string> = {
  en: "Regression parameters used in analysis:",
  cs: "Regresní parametry použité v analýze:",
  sk: "Regresné parametre použité v analýze:",
};

export const REGRESSION_OPTIONS: RegressionOption[] = [
  {
    value: "1",
    parameters: {
      en: "Age, Years of service, Years of education, Years of experience, Years of experience squared, Seniority",
      cs: "Věk, Délka zaměstnání, Počet let vzdělání, Počet let praxe, Počet let praxe na druhou, Seniorita",
      sk: "Vek, Dĺžka zamestnania, Počet rokov vzdelania, Počet rokov praxe, Počet rokov praxe na druhú, Seniorita",
    },
  },
  {
    value: "2",
    parameters: {
      en: "Years of service, Years of education, Years of experience, Seniority",
      cs: "Délka zaměstnání, Počet let vzdělání, Počet let praxe, Seniorita",
      sk: "Dĺžka zamestnania, Počet rokov vzdelania, Počet rokov praxe, Seniorita",
    },
  },
  {
    value: "3",
    parameters: {
      en: "Age, Years of service, Years of education, Years of experience, Seniority",
      cs: "Věk, Délka zaměstnání, Počet let vzdělání, Počet let praxe, Seniorita",
      sk: "Vek, Dĺžka zamestnania, Počet rokov vzdelania, Počet rokov praxe, Seniorita",
    },
  },
  {
    value: "4",
    parameters: {
      en: "Years of service, Years of education, Years of experience, Seniority, Employee scarcity, Employee evaluation",
      cs: "Délka zaměstnání, Počet let vzdělání, Počet let praxe, Seniorita, Nedostatek zaměstnanců, Hodnocení zaměstnance",
      sk: "Dĺžka zamestnania, Počet rokov vzdelania, Počet rokov praxe, Seniorita, Nedostatok zamestnancov, Hodnotenie zamestnanca",
    },
  },
  {
    value: "5",
    parameters: {
      en: "Years of service, Years of education, Years of experience, Seniority, Employee scarcity, Employee evaluation, Employee contribution",
      cs: "Délka zaměstnání, Počet let vzdělání, Počet let praxe, Seniorita, Nedostatek zaměstnanců, Hodnocení zaměstnance, Příspěvek zaměstnance",
      sk: "Dĺžka zamestnania, Počet rokov vzdelania, Počet rokov praxe, Seniorita, Nedostatok zamestnancov, Hodnotenie zamestnanca, Príspevok zamestnanca",
    },
  },
  {
    value: "6",
    parameters: {
      en: "Years of service, Years of education, Years of experience, Seniority, Employee contribution",
      cs: "Délka zaměstnání, Počet let vzdělání, Počet let praxe, Seniorita, Příspěvek zaměstnance",
      sk: "Dĺžka zamestnania, Počet rokov vzdelania, Počet rokov praxe, Seniorita, Príspevok zamestnanca",
    },
  },
];

// ── Salary Component Labels (for Grade Chart filter) ──

export type SalaryComponentType = "net" | "basic" | "variable";

export interface SalaryComponentOption {
  value: SalaryComponentType;
  label: { en: string; cs: string; sk: string };
  tooltip: { en: string; cs: string; sk: string };
}

export const SALARY_COMPONENT_OPTIONS: SalaryComponentOption[] = [
  {
    value: "net",
    label: { en: "Net Salary", cs: "Celková mzda/plat", sk: "Celková mzda/plat" },
    tooltip: { en: "", cs: "", sk: "" },
  },
  {
    value: "basic",
    label: { en: "Basic Salary", cs: "Základní mzda/plat", sk: "Základná mzda/plat" },
    tooltip: { en: "", cs: "", sk: "" },
  },
  {
    value: "variable",
    label: { en: "Variable Salary", cs: "Variabilní mzda/plat", sk: "Variabilná mzda/plat" },
    tooltip: {
      en: "bonuses, allowances, special payments",
      cs: "bonusy, příspěvky, mimořádné platby",
      sk: "bonusy, príspevky, mimoriadne platby",
    },
  },
];

/**
 * Get translated label for a salary component.
 */
export const getSalaryComponentLabel = (
  value: SalaryComponentType,
  lang: string,
): string => {
  const option = SALARY_COMPONENT_OPTIONS.find((o) => o.value === value);

  if (!option) return "";

  const normalized = lang?.split("-")[0] ?? "en";

  if (normalized === "cs") return option.label.cs;
  if (normalized === "sk") return option.label.sk;

  return option.label.en;
};

/**
 * Get translated tooltip for a salary component (e.g. variable salary details).
 */
export const getSalaryComponentTooltip = (
  value: SalaryComponentType,
  lang: string,
): string => {
  const option = SALARY_COMPONENT_OPTIONS.find((o) => o.value === value);

  if (!option) return "";

  const normalized = lang?.split("-")[0] ?? "en";

  if (normalized === "cs") return option.tooltip.cs;
  if (normalized === "sk") return option.tooltip.sk;

  return option.tooltip.en;
};

/**
 * Normalize language code to one of the supported languages (en, cs, sk).
 */
const normalizeLang = (lang: string): string => {
  const base = lang?.split("-")[0] ?? "en";

  if (base === "cs" || base === "sk") return base;

  return "en";
};

/**
 * Get regression parameters label for a given option value and language code.
 */
export const getRegressionParametersByLang = (
  value: string,
  lang: string,
): string => {
  const option = REGRESSION_OPTIONS.find((opt) => opt.value === value);

  if (!option) return "";

  const normalized = normalizeLang(lang);

  return option.parameters[normalized as keyof typeof option.parameters];
};

/**
 * Get translated dropdown label for "Regression Parameters".
 */
export const getRegressionDropdownLabel = (lang: string): string =>
  REGRESSION_DROPDOWN_LABEL[normalizeLang(lang)] ?? REGRESSION_DROPDOWN_LABEL.en;

/**
 * Get translated "Option" prefix for dropdown items.
 */
export const getRegressionOptionPrefix = (lang: string): string =>
  REGRESSION_OPTION_PREFIX[normalizeLang(lang)] ?? REGRESSION_OPTION_PREFIX.en;

/**
 * Get translated note label for "Regression parameters used in analysis:".
 */
export const getRegressionNoteLabel = (lang: string): string =>
  REGRESSION_NOTE_LABEL[normalizeLang(lang)] ?? REGRESSION_NOTE_LABEL.en;
